/**
 * Subscription service using RevenueCat.
 * 
 * Handles:
 * - SDK initialization
 * - Product fetching
 * - Purchase flow
 * - Restore purchases
 * - Subscription status checking
 */

import Purchases, { PurchasesPackage, CustomerInfo } from 'react-native-purchases';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { API_BASE_URL } from '@/utils/apiConfig';
import { secureTokenStorage } from './secureTokenStorage';

export type SubscriptionTier = 'trial' | 'basic' | 'full' | 'extended';
export type SubscriptionPeriod = 'monthly' | 'annual';

export class SubscriptionService {
  private initialized = false;
  /** The userId that RevenueCat was last configured/logged-in with. */
  private _initializedUserId: string | null = null;
  /** Mutex: if non-null, initialization is already in progress — await this promise instead of starting a new one. */
  private _initPromise: Promise<void> | null = null;

  /** Reset initialization state so the next initialize() call will try again (e.g. after "Try again" tap). */
  reset() {
    this.initialized = false;
    this._initializedUserId = null;
    this._initPromise = null;
    console.log('🔄 RevenueCat: Reset initialization state for retry');
  }
  
  async initialize(userId: string): Promise<void> {
    // Already initialized for this exact user — no-op.
    if (this.initialized && this._initializedUserId === userId) return;

    // Different user logged in while SDK is already initialized — switch user via logIn()
    // without re-calling configure() (which would crash the native SDK).
    if (this.initialized && this._initializedUserId !== userId) {
      try {
        console.log(`🔄 RevenueCat: Switching user from ${this._initializedUserId} → ${userId}`);
        await Purchases.logIn(userId);
        this._initializedUserId = userId;
        console.log('✅ RevenueCat: User switched successfully');
      } catch (e) {
        console.error('❌ RevenueCat: logIn (user switch) failed', e);
      }
      return;
    }

    // Mutex: if another initialize() call is already in-flight, wait for it instead of
    // starting a second concurrent configure() — multiple configure() calls crash the native SDK.
    if (this._initPromise) {
      console.log('🔄 RevenueCat: Initialization already in progress, waiting for it to complete…');
      await this._initPromise;
      return;
    }

    this._initPromise = this._doInitialize(userId).finally(() => {
      this._initPromise = null;
    });
    await this._initPromise;
  }

  private async _doInitialize(userId: string): Promise<void> {
    // Double-check inside the mutex (another awaiter may have completed initialization).
    if (this.initialized) return;

    const platform = Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : null;
    if (!platform) return;

    console.log(`🚀 RevenueCat: Starting initialization for ${platform}, userId: ${userId}`);

    const apiKey = await this.getRevenueCatKey(platform);
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
      console.warn(
        '⚠️ RevenueCat: No API key available (backend may have returned 500 or key missing). ' +
        'Subscription features disabled. In dev, set EXPO_PUBLIC_REVENUECAT_IOS_API_KEY or EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY to test.'
      );
      return;
    }

    try {
      await Purchases.configure({
        apiKey: apiKey.trim(),
        appUserID: userId,
      });
      this.initialized = true;
      this._initializedUserId = userId;
      console.log('✅ RevenueCat initialized for platform:', platform, '| userId:', userId);
    } catch (e) {
      console.error('❌ RevenueCat: configure failed', e);
    }
  }

  /**
   * Get RevenueCat API key.
   *
   * Priority order:
   *   1. EXPO_PUBLIC env vars (baked in at EAS build time) — always available, no auth needed
   *   2. Constants.expoConfig.extra (app.config.js extras)
   *   3. Authenticated backend fetch (AWS Secrets Manager) — fallback when running dev server
   *
   * RevenueCat API keys are public client identifiers — safe to embed directly in the app.
   * The authenticated backend route is kept as a fallback for local dev, but the build-time
   * env vars are the primary source so the paywall works before the user's JWT is available.
   */
  private async getRevenueCatKey(platform: 'ios' | 'android'): Promise<string> {
    // 1. Fastest path: EXPO_PUBLIC env vars baked in at EAS build time
    const envKey =
      platform === 'ios'
        ? process.env.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY
        : process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY;
    if (envKey && typeof envKey === 'string' && envKey.trim()) {
      console.log(`✅ RevenueCat: Got API key from EXPO_PUBLIC env var for ${platform}`);
      return envKey.trim();
    }

    // 2. app.config.js extras (useful for local dev with .env file)
    const extra = Constants.expoConfig?.extra as { revenueCatIosApiKey?: string; revenueCatAndroidApiKey?: string } | undefined;
    const extraKey = platform === 'ios' ? extra?.revenueCatIosApiKey : extra?.revenueCatAndroidApiKey;
    if (extraKey && typeof extraKey === 'string' && extraKey.trim()) {
      console.log(`✅ RevenueCat: Got API key from expo config extra for ${platform}`);
      return extraKey.trim();
    }

    // 3. Authenticated backend fetch (fallback for local dev without env vars)
    try {
      const token = await secureTokenStorage.getCedarJwt();
      console.log(`🔑 RevenueCat: Attempting backend fetch. JWT ${token ? `present (${token.length} chars)` : 'MISSING'}`);

      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const url = `${API_BASE_URL}/api/config/revenuecat-key?platform=${platform}`;
      const response = await fetch(url, { headers });
      console.log(`🔑 RevenueCat: Backend response status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        const key = data?.api_key;
        if (key && typeof key === 'string' && key.trim()) {
          console.log('✅ RevenueCat: Got API key from backend');
          return key.trim();
        }
      } else {
        let errorBody = '';
        try { errorBody = await response.text(); } catch { /* ignore */ }
        console.warn(`⚠️ RevenueCat: Backend returned ${response.status}. Body: ${errorBody.slice(0, 200)}`);
      }
    } catch (e) {
      console.warn('⚠️ RevenueCat: Backend key fetch exception:', e);
    }

    console.error(`❌ RevenueCat: No API key found for ${platform}. Ensure EXPO_PUBLIC_REVENUECAT_${platform.toUpperCase()}_API_KEY is set in eas.json.`);
    return '';
  }
  
  /**
   * Report that the PaywallModal target product was not found in the packages
   * returned by getOfferings(). Records the actual `product.identifier` strings the
   * store SDK returned in the device's console log (via _postDiagnostic, which no
   * longer reaches the backend — see its note), for this specific failure mode
   * (packages present but target not among them).
   */
  async reportTargetProductNotFound(data: {
    target_product_id: string;
    available_product_ids: string[];
  }): Promise<void> {
    await this._postDiagnostic({
      offerings_count: -1,
      offerings_keys: [],
      current_offering: null,
      packages_count: data.available_product_ids.length,
      error_message:
        `target_product_not_found | target="${data.target_product_id}" | ` +
        `available=[${data.available_product_ids.join(', ')}]`,
    });
  }

  /**
   * Record a RevenueCat offerings failure on the device (the console the device logs keep).
   *
   * Until 2026-09-30 this also POSTed to /api/debug/revenuecat-diagnostic with the user's
   * JWT. That route is Admin-Portal-session only since 2026-09-29 (no user JWT grants
   * admin), so the call was a 401 for every user: fire-and-forget (every caller uses
   * `void`), its response never read, its errors swallowed. It is gone (review of #4246
   * LOW-5); nothing here touches the network. Never throws.
   */
  private async _postDiagnostic(data: {
    offerings_count: number;
    offerings_keys: string[];
    current_offering: string | null;
    packages_count: number;
    error_message: string | null;
  }): Promise<void> {
    try {
      console.warn(
        `🔬 RevenueCat diagnostic: offerings=${data.offerings_count} packages=${data.packages_count} `
        + `current=${data.current_offering ?? 'null'} error=${data.error_message ?? 'none'}`,
      );
    } catch {
      // Diagnostics must never break the app.
    }
  }

  async getOfferings(): Promise<PurchasesPackage[]> {
    if (!this.initialized) {
      console.warn('⚠️ RevenueCat: getOfferings called but not initialized — returning empty');
      return [];
    }
    try {
      console.log('📦 RevenueCat: Fetching offerings from App Store/Play...');
      const offerings = await Purchases.getOfferings();

      // Log the full offerings summary for diagnostics
      const allKeys = Object.keys(offerings.all ?? {});
      console.log(`📦 RevenueCat: Total offerings in catalog: ${allKeys.length} — keys: [${allKeys.join(', ')}]`);
      console.log(`📦 RevenueCat: current offering identifier: ${offerings.current?.identifier ?? 'NULL — StoreKit may not have resolved products yet'}`);

      // Collect packages from ALL offerings so every tier is available,
      // even if basic/essentials is in a separate RevenueCat offering.
      const seenProductIds = new Set<string>();
      const allPackages: PurchasesPackage[] = [];

      for (const key of allKeys) {
        const off = offerings.all[key];
        for (const pkg of off.availablePackages) {
          if (!seenProductIds.has(pkg.product.identifier)) {
            seenProductIds.add(pkg.product.identifier);
            allPackages.push(pkg);
          }
        }
      }

      console.log(`📦 RevenueCat: Found ${allPackages.length} unique packages across ${allKeys.length} offerings`);
      allPackages.forEach((p) =>
        console.log(`   • ${p.identifier} | productId: ${p.product.identifier} | price: ${p.product.priceString} | currency: ${p.product.currencyCode}`)
      );

      if (allPackages.length > 0) {
        return allPackages;
      }

      if (offerings.current) {
        console.warn(
          '⚠️ RevenueCat: offerings exist but no packages resolved.\n' +
          '   This means StoreKit / Play Billing could not resolve the underlying store products. Common causes:\n' +
          '   1. Products are "Ready to Submit"/"Inactive" but NOT "Cleared for Sale"/"Active" in the store console.\n' +
          '   2. Products were recently changed — stores can take up to 24 hours to propagate.\n' +
          '   3. Sandbox / tester account is not configured correctly on this device.\n' +
          '   Expected iOS product IDs:     com.coachcedar.app.{basic|full|extended}.{monthly|annual}\n' +
          '   Expected Android product IDs: {basic|full|extended}_{monthly|annual}:{basePlanId}'
        );
        void this._postDiagnostic({
          offerings_count: allKeys.length,
          offerings_keys: allKeys,
          current_offering: offerings.current.identifier,
          packages_count: 0,
          error_message: 'offerings exist but no packages resolved across all offerings',
        });
        return [];
      }

      // offerings.current is null — log all offerings for debugging
      allKeys.forEach((key) => {
        const off = offerings.all[key];
        console.warn(`   Offering "${key}": availablePackages.length = ${off.availablePackages.length}`);
      });
      console.warn(
        '⚠️ RevenueCat: offerings.current is NULL.\n' +
        '   This is usually caused by App Store Connect products not being "Cleared for Sale".\n' +
        '   RevenueCat sets current=null when none of the products in the Current Offering\n' +
        '   can be verified by StoreKit on this device.'
      );

      // Post diagnostic so we can see this in CloudWatch
      const offeringsDetail = allKeys
        .map((k) => `${k}:${offerings.all[k].availablePackages.length}pkgs`)
        .join(', ');
      void this._postDiagnostic({
        offerings_count: allKeys.length,
        offerings_keys: allKeys,
        current_offering: null,
        packages_count: -1,
        error_message: `offerings.current is NULL. All offerings: [${offeringsDetail || 'none'}]`,
      });

      return [];
    } catch (error: any) {
      console.error('❌ RevenueCat: Failed to fetch offerings', error);
      // Post diagnostic for exception path too
      void this._postDiagnostic({
        offerings_count: -1,
        offerings_keys: [],
        current_offering: null,
        packages_count: -1,
        error_message: `Exception: ${error?.message ?? String(error)}`,
      });
      return [];
    }
  }
  
  async purchasePackage(pkg: PurchasesPackage): Promise<{
    success: boolean;
    tier?: SubscriptionTier;
    error?: string;
    cancelled?: boolean;
    alreadySubscribed?: boolean;
  }> {
    if (!this.initialized) {
      return { success: false, error: 'Subscription not available (RevenueCat not configured)' };
    }
    try {
      console.log('💳 RevenueCat: Purchasing package', pkg.identifier);
      const { customerInfo } = await Purchases.purchasePackage(pkg);

      if (!customerInfo?.entitlements) {
        console.error('❌ RevenueCat: purchasePackage returned null customerInfo');
        return { success: false, error: 'Purchase completed but subscription status unavailable' };
      }

      // Debug: log all active entitlements to diagnose mismatches
      const activeKeys = Object.keys(customerInfo.entitlements.active);
      const allKeys = Object.keys(customerInfo.entitlements.all);
      if (__DEV__) {
        console.log('🔑 RevenueCat entitlements.active keys:', JSON.stringify(activeKeys));
        console.log('🔑 RevenueCat entitlements.all keys:', JSON.stringify(allKeys));
        console.log('🔑 RevenueCat activeSubscriptions:', JSON.stringify(customerInfo.activeSubscriptions));
      }

      // Check which entitlement is active (highest tier wins)
      if (customerInfo.entitlements.active['extended_plan']) {
        return { success: true, tier: 'extended' };
      } else if (customerInfo.entitlements.active['full_plan']) {
        return { success: true, tier: 'full' };
      } else if (customerInfo.entitlements.active['basic_plan']) {
        return { success: true, tier: 'basic' };
      } else if (activeKeys.length > 0) {
        // Unknown entitlement name — treat any active entitlement as 'basic' so the
        // gate opens even if the RevenueCat dashboard uses a different entitlement id.
        console.warn(`⚠️ RevenueCat: Unrecognized entitlement keys [${activeKeys.join(', ')}] — unlocking as 'basic'`);
        return { success: true, tier: 'basic' };
      } else if (customerInfo.activeSubscriptions.length > 0) {
        // No entitlements but there ARE active subscriptions — product may not be
        // linked to an entitlement in the RevenueCat dashboard.  Infer tier from
        // the product identifier so the gate still opens.
        const activeSub = customerInfo.activeSubscriptions[0];
        const sub = activeSub.toLowerCase();
        const tier: SubscriptionTier = sub.includes('extended') ? 'extended' : sub.includes('full') ? 'full' : 'basic';
        console.warn(`⚠️ RevenueCat: No entitlements but activeSubscriptions=[${customerInfo.activeSubscriptions.join(', ')}] — unlocking as '${tier}'`);
        return { success: true, tier };
      } else {
        const hint = 'No active entitlements or subscriptions returned — product may not be linked to an entitlement in RevenueCat dashboard';
        console.error('❌ Entitlement check failed:', hint);
        return { success: false, error: `Entitlement not activated. ${hint}` };
      }
    } catch (e: any) {
      if (e.userCancelled) {
        console.log('⏸️ RevenueCat: User cancelled purchase');
        return { success: false, cancelled: true };
      }
      // RevenueCat error code 7 = PRODUCT_ALREADY_PURCHASED
      // The user already owns this subscription — treat it like a successful
      // restore so the paywall can close gracefully.
      if (e.code === 7 || e.code === 'PRODUCT_ALREADY_PURCHASED') {
        console.log('ℹ️ RevenueCat: Product already purchased — reporting alreadySubscribed');
        return { success: false, alreadySubscribed: true };
      }
      console.error('❌ RevenueCat: Purchase failed', e);
      return { success: false, error: e.message };
    }
  }
  
  async restorePurchases(): Promise<{
    success: boolean;
    tier?: SubscriptionTier;
    error?: string;
  }> {
    if (!this.initialized) {
      return { success: false, error: 'Subscription not available (RevenueCat not configured)' };
    }
    try {
      console.log('🔄 RevenueCat: Restoring purchases');
      const customerInfo = await Purchases.restorePurchases();

      if (!customerInfo?.entitlements) {
        console.error('❌ RevenueCat: restorePurchases returned null customerInfo');
        return { success: false, error: 'Could not retrieve subscription status' };
      }

      // Debug: log entitlements after restore
      const activeKeys = Object.keys(customerInfo.entitlements.active);
      if (__DEV__) {
        console.log('🔑 RevenueCat restore — entitlements.active:', JSON.stringify(activeKeys));
        console.log('🔑 RevenueCat restore — activeSubscriptions:', JSON.stringify(customerInfo.activeSubscriptions));
      }

      // Check entitlements (highest tier wins)
      if (customerInfo.entitlements.active['extended_plan']) {
        return { success: true, tier: 'extended' };
      } else if (customerInfo.entitlements.active['full_plan']) {
        return { success: true, tier: 'full' };
      } else if (customerInfo.entitlements.active['basic_plan']) {
        return { success: true, tier: 'basic' };
      } else if (activeKeys.length > 0) {
        // Unknown entitlement name — treat any active entitlement as 'basic'
        console.warn(`⚠️ RevenueCat restore: Unrecognized entitlement keys [${activeKeys.join(', ')}] — unlocking as 'basic'`);
        return { success: true, tier: 'basic' };
      } else if (customerInfo.activeSubscriptions.length > 0) {
        // No entitlements but active subscription — infer tier from product id
        const activeSub = customerInfo.activeSubscriptions[0];
        const sub = activeSub.toLowerCase();
        const tier: SubscriptionTier = sub.includes('extended') ? 'extended' : sub.includes('full') ? 'full' : 'basic';
        console.warn(`⚠️ RevenueCat restore: No entitlements but activeSubscriptions present — unlocking as '${tier}'`);
        return { success: true, tier };
      } else {
        return { success: false, error: 'No active subscriptions found' };
      }
    } catch (error: any) {
      console.error('❌ RevenueCat: Restore failed', error);
      return { success: false, error: error.message };
    }
  }
  
  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.initialized) return null;
    try {
      return await Purchases.getCustomerInfo();
    } catch (error) {
      console.error('❌ RevenueCat: Failed to get customer info', error);
      return null;
    }
  }
  
  /**
   * Log out from RevenueCat, detaching the current customer profile.
   * Must be called during account deletion so the next sign-up on this
   * device starts with a clean RevenueCat anonymous user — otherwise
   * the old customer profile (and its trial/subscription state) persists.
   */
  async logOut(): Promise<void> {
    try {
      // Wait for any in-flight init to complete before logging out
      if (this._initPromise) await this._initPromise;
      if (this.initialized) await Purchases.logOut();
      console.log('✅ RevenueCat: Logged out — customer profile detached');
    } catch (error) {
      console.error('⚠️ RevenueCat: logOut failed (non-critical):', error);
    } finally {
      this.initialized = false;
      this._initializedUserId = null;
      this._initPromise = null;
    }
  }

  async getCurrentTier(): Promise<SubscriptionTier> {
    if (Platform.OS === 'web') {
      try {
        const base = typeof window !== 'undefined' ? '' : API_BASE_URL;
        const response = await fetch(`${base}/api/user/subscription-status`);
        if (!response.ok) return 'trial';
        const data = await response.json();
        return (data.tier as SubscriptionTier) ?? 'trial';
      } catch {
        return 'trial';
      }
    }

    // Mobile: RevenueCat (skip if never initialized, e.g. missing API key)
    if (!this.initialized) return 'trial';
    const customerInfo = await this.getCustomerInfo();
    if (!customerInfo) return 'trial';

    if (customerInfo.entitlements.active['extended_plan']) return 'extended';
    if (customerInfo.entitlements.active['full_plan']) return 'full';
    if (customerInfo.entitlements.active['basic_plan']) return 'basic';
    return 'trial';
  }
}

export const subscriptionService = new SubscriptionService();
